import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format, parseISO, addDays, startOfYear, endOfYear, isWeekend, isSameDay, isWithinInterval } from 'date-fns';
import { de } from 'date-fns/locale';

const {
  FiCalendar,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSave,
  FiX,
  FiRefreshCw,
  FiLoader,
  FiAlertCircle,
  FiCheck,
  FiInfo,
  FiToggleLeft,
  FiToggleRight,
  FiCoffee,
  FiSun,
  FiUmbrella,
  FiClock,
  FiFilter
} = FiIcons;

const HolidayConfiguration = () => {
  const [holidays, setHolidays] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [filterType, setFilterType] = useState('all'); // 'all', 'holiday', 'vacation'
  
  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: '',
    is_active: true,
    is_recurring: false,
    holiday_type: 'holiday',
    description: ''
  });
  
  const [errors, setErrors] = useState({});

  // Vordefinierte Schweizer Feiertage für einfaches Hinzufügen
  const swissHolidays = [
    { name: 'Neujahr', start_date: `${currentYear}-01-01`, is_recurring: true },
    { name: 'Berchtoldstag', start_date: `${currentYear}-01-02`, is_recurring: true },
    { name: 'Karfreitag', start_date: calculateEaster(currentYear, -2), is_recurring: true },
    { name: 'Ostermontag', start_date: calculateEaster(currentYear, 1), is_recurring: true },
    { name: 'Tag der Arbeit', start_date: `${currentYear}-05-01`, is_recurring: true },
    { name: 'Auffahrt', start_date: calculateEaster(currentYear, 39), is_recurring: true },
    { name: 'Pfingstmontag', start_date: calculateEaster(currentYear, 50), is_recurring: true },
    { name: 'Bundesfeiertag', start_date: `${currentYear}-08-01`, is_recurring: true },
    { name: 'Weihnachten', start_date: `${currentYear}-12-25`, is_recurring: true },
    { name: 'Stephanstag', start_date: `${currentYear}-12-26`, is_recurring: true }
  ];

  // Vordefinierte Ferienperioden
  const commonVacations = [
    { 
      name: 'Sommerferien', 
      start_date: `${currentYear}-07-15`, 
      end_date: `${currentYear}-08-15`, 
      is_recurring: true,
      description: 'Betriebsferien Sommer'
    },
    { 
      name: 'Herbstferien', 
      start_date: `${currentYear}-10-09`, 
      end_date: `${currentYear}-10-20`, 
      is_recurring: true,
      description: 'Betriebsferien Herbst'
    },
    { 
      name: 'Weihnachtsferien', 
      start_date: `${currentYear}-12-23`, 
      end_date: `${currentYear + 1}-01-06`, 
      is_recurring: true,
      description: 'Betriebsferien Weihnachten/Neujahr'
    },
    { 
      name: 'Frühlingsferien', 
      start_date: `${currentYear}-04-08`, 
      end_date: `${currentYear}-04-19`, 
      is_recurring: true,
      description: 'Betriebsferien Frühling'
    }
  ];

  // Oster-Berechnung (vereinfacht)
  function calculateEaster(year, offset = 0) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    
    const easterDate = new Date(year, month - 1, day);
    const targetDate = addDays(easterDate, offset);
    return format(targetDate, 'yyyy-MM-dd');
  }

  // Feiertage und Ferien laden
  const loadHolidays = async () => {
    setIsLoading(true);
    try {
      console.log('Loading holidays and vacations...');
      const { data, error } = await supabase
        .from('holidays_a7b9c8d6e5')
        .select('*')
        .order('start_date', { ascending: true });

      if (error) {
        console.error('Error loading holidays:', error);
        throw error;
      }

      console.log('Loaded holidays and vacations:', data);
      setHolidays(data || []);
    } catch (error) {
      console.error('Error in loadHolidays:', error);
      setMessage(`Fehler beim Laden: ${error.message}`);
      setMessageType('error');
    } finally {
      setIsLoading(false);
    }
  };

  // Beim ersten Laden
  useEffect(() => {
    loadHolidays();
  }, []);

  // Formular validieren
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }
    
    if (!formData.start_date) {
      newErrors.start_date = 'Startdatum ist erforderlich';
    } else {
      const selectedDate = parseISO(formData.start_date);
      if (isNaN(selectedDate.getTime())) {
        newErrors.start_date = 'Ungültiges Startdatum';
      }
    }

    // Für Ferienperioden: Enddatum validieren
    if (formData.holiday_type === 'vacation') {
      if (!formData.end_date) {
        newErrors.end_date = 'Enddatum ist für Ferienperioden erforderlich';
      } else {
        const startDate = parseISO(formData.start_date);
        const endDate = parseISO(formData.end_date);
        
        if (isNaN(endDate.getTime())) {
          newErrors.end_date = 'Ungültiges Enddatum';
        } else if (endDate < startDate) {
          newErrors.end_date = 'Enddatum muss nach dem Startdatum liegen';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Feiertag/Ferien erstellen/bearbeiten
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const submitData = {
        name: formData.name,
        start_date: formData.start_date,
        end_date: formData.holiday_type === 'vacation' ? formData.end_date : null,
        is_active: formData.is_active,
        is_recurring: formData.is_recurring,
        holiday_type: formData.holiday_type,
        description: formData.description,
        updated_at: new Date().toISOString()
      };

      if (editingHoliday) {
        // Aktualisieren
        const { error } = await supabase
          .from('holidays_a7b9c8d6e5')
          .update(submitData)
          .eq('id', editingHoliday.id);

        if (error) throw error;
        setMessage(`${formData.holiday_type === 'vacation' ? 'Ferienperiode' : 'Feiertag'} erfolgreich aktualisiert!`);
      } else {
        // Neu erstellen
        submitData.created_at = new Date().toISOString();
        const { error } = await supabase
          .from('holidays_a7b9c8d6e5')
          .insert(submitData);

        if (error) throw error;
        setMessage(`${formData.holiday_type === 'vacation' ? 'Ferienperiode' : 'Feiertag'} erfolgreich erstellt!`);
      }

      setMessageType('success');
      handleCancel();
      await loadHolidays();
      
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 3000);
    } catch (error) {
      console.error('Error saving holiday:', error);
      setMessage(`Fehler beim Speichern: ${error.message}`);
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Feiertag/Ferien löschen
  const handleDelete = async (holidayId) => {
    if (!confirm('Sind Sie sicher, dass Sie diesen Eintrag löschen möchten?')) return;

    try {
      const { error } = await supabase
        .from('holidays_a7b9c8d6e5')
        .delete()
        .eq('id', holidayId);

      if (error) throw error;

      setMessage('Eintrag erfolgreich gelöscht!');
      setMessageType('success');
      await loadHolidays();
      
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 2000);
    } catch (error) {
      console.error('Error deleting holiday:', error);
      setMessage(`Fehler beim Löschen: ${error.message}`);
      setMessageType('error');
    }
  };

  // Aktivieren/deaktivieren
  const toggleHoliday = async (holiday) => {
    try {
      const { error } = await supabase
        .from('holidays_a7b9c8d6e5')
        .update({
          is_active: !holiday.is_active,
          updated_at: new Date().toISOString()
        })
        .eq('id', holiday.id);

      if (error) throw error;

      await loadHolidays();
    } catch (error) {
      console.error('Error toggling holiday:', error);
      setMessage(`Fehler beim Aktualisieren: ${error.message}`);
      setMessageType('error');
    }
  };

  // Schweizer Feiertag hinzufügen
  const addSwissHoliday = async (holiday) => {
    try {
      // Prüfen ob bereits vorhanden
      const existingHoliday = holidays.find(h => 
        h.name === holiday.name && 
        h.start_date === holiday.start_date
      );

      if (existingHoliday) {
        setMessage(`${holiday.name} ist bereits vorhanden`);
        setMessageType('error');
        return;
      }

      const { error } = await supabase
        .from('holidays_a7b9c8d6e5')
        .insert({
          name: holiday.name,
          start_date: holiday.start_date,
          end_date: null,
          is_active: true,
          is_recurring: holiday.is_recurring,
          holiday_type: 'holiday',
          description: `Schweizer Feiertag (${currentYear})`,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      setMessage(`${holiday.name} erfolgreich hinzugefügt!`);
      setMessageType('success');
      await loadHolidays();
      
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 2000);
    } catch (error) {
      console.error('Error adding Swiss holiday:', error);
      setMessage(`Fehler beim Hinzufügen: ${error.message}`);
      setMessageType('error');
    }
  };

  // Ferienperiode hinzufügen
  const addCommonVacation = async (vacation) => {
    try {
      // Prüfen ob bereits vorhanden
      const existingVacation = holidays.find(h => 
        h.name === vacation.name && 
        h.start_date === vacation.start_date &&
        h.end_date === vacation.end_date
      );

      if (existingVacation) {
        setMessage(`${vacation.name} ist bereits vorhanden`);
        setMessageType('error');
        return;
      }

      const { error } = await supabase
        .from('holidays_a7b9c8d6e5')
        .insert({
          name: vacation.name,
          start_date: vacation.start_date,
          end_date: vacation.end_date,
          is_active: true,
          is_recurring: vacation.is_recurring,
          holiday_type: 'vacation',
          description: vacation.description,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      setMessage(`${vacation.name} erfolgreich hinzugefügt!`);
      setMessageType('success');
      await loadHolidays();
      
      setTimeout(() => {
        setMessage('');
        setMessageType('');
      }, 2000);
    } catch (error) {
      console.error('Error adding vacation:', error);
      setMessage(`Fehler beim Hinzufügen: ${error.message}`);
      setMessageType('error');
    }
  };

  // Bearbeitung starten
  const handleEdit = (holiday) => {
    setEditingHoliday(holiday);
    setFormData({
      name: holiday.name,
      start_date: holiday.start_date,
      end_date: holiday.end_date || '',
      is_active: holiday.is_active,
      is_recurring: holiday.is_recurring,
      holiday_type: holiday.holiday_type || 'holiday',
      description: holiday.description || ''
    });
    setIsCreating(true);
  };

  // Formular zurücksetzen
  const handleCancel = () => {
    setIsCreating(false);
    setEditingHoliday(null);
    setFormData({
      name: '',
      start_date: '',
      end_date: '',
      is_active: true,
      is_recurring: false,
      holiday_type: 'holiday',
      description: ''
    });
    setErrors({});
  };

  // Input-Änderungen verarbeiten
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Aktuelle Einträge filtern
  const currentYearEntries = holidays.filter(holiday => {
    // Jahr-Filter
    const yearMatch = holiday.start_date.startsWith(currentYear.toString()) ||
                     (holiday.end_date && holiday.end_date.startsWith(currentYear.toString()));
    
    // Typ-Filter
    const typeMatch = filterType === 'all' || holiday.holiday_type === filterType;
    
    return yearMatch && typeMatch;
  });

  const activeEntries = currentYearEntries.filter(h => h.is_active);

  // Formatiere Datumsbereich für Anzeige
  const formatDateRange = (entry) => {
    const startDate = format(parseISO(entry.start_date), 'd. MMMM yyyy', { locale: de });
    
    if (entry.end_date) {
      const endDate = format(parseISO(entry.end_date), 'd. MMMM yyyy', { locale: de });
      return `${startDate} - ${endDate}`;
    }
    
    return startDate;
  };

  // Berechne Anzahl Tage für Ferienperioden
  const calculateDuration = (entry) => {
    if (!entry.end_date) return 1;
    
    const start = parseISO(entry.start_date);
    const end = parseISO(entry.end_date);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    
    return diffDays;
  };

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Feiertags- & Ferien-Verwaltung</h1>
          <p className="text-gray-600">Verwalten Sie Feiertage und Ferienperioden für Menü-Blockierungen</p>
        </div>
        <div className="flex space-x-3">
          <motion.button
            onClick={loadHolidays}
            disabled={isLoading}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`bg-gray-100 text-gray-700 px-4 py-2 rounded-xl font-semibold hover:bg-gray-200 transition-colors duration-300 flex items-center space-x-2 ${
              isLoading ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            <SafeIcon icon={FiRefreshCw} className={isLoading ? 'animate-spin' : ''} />
            <span>Aktualisieren</span>
          </motion.button>
          <motion.button
            onClick={() => setIsCreating(true)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-primary-500 text-white px-4 py-2 rounded-xl font-semibold hover:bg-primary-600 transition-colors duration-300 flex items-center space-x-2"
          >
            <SafeIcon icon={FiPlus} />
            <span>Neuer Eintrag</span>
          </motion.button>
        </div>
      </div>

      {/* Status-Nachricht */}
      {message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-6 p-4 rounded-xl flex items-center ${
            messageType === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          <SafeIcon icon={messageType === 'success' ? FiCheck : FiAlertCircle} className="mr-2" />
          <span>{message}</span>
        </motion.div>
      )}

      {/* Jahr-Auswahl und Filter */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-blue-800">
              <SafeIcon icon={FiCalendar} />
              <span className="font-medium">Jahr:</span>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setCurrentYear(currentYear - 1)}
                className="bg-blue-500 text-white px-3 py-1 rounded-lg hover:bg-blue-600 transition-colors"
              >
                {currentYear - 1}
              </button>
              <span className="font-bold text-blue-800 text-lg">{currentYear}</span>
              <button
                onClick={() => setCurrentYear(currentYear + 1)}
                className="bg-blue-500 text-white px-3 py-1 rounded-lg hover:bg-blue-600 transition-colors"
              >
                {currentYear + 1}
              </button>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-blue-800">
              <SafeIcon icon={FiFilter} />
              <span className="font-medium">Filter:</span>
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="bg-white border border-blue-300 rounded-lg px-3 py-1 text-blue-800"
            >
              <option value="all">Alle</option>
              <option value="holiday">Nur Feiertage</option>
              <option value="vacation">Nur Ferien</option>
            </select>
          </div>
        </div>
        
        <div className="mt-2 text-blue-700 text-sm">
          <strong>{activeEntries.length} aktive Einträge</strong> für {currentYear} 
          {filterType !== 'all' && ` (${filterType === 'holiday' ? 'Feiertage' : 'Ferien'})`}
        </div>
      </div>

      {/* Schweizer Feiertage hinzufügen */}
      <div className="mb-8 bg-white rounded-2xl shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <SafeIcon icon={FiSun} className="mr-2 text-orange-500" />
          Schweizer Feiertage {currentYear}
        </h2>
        <p className="text-gray-600 mb-4">
          Fügen Sie schnell die offiziellen Schweizer Feiertage hinzu:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {swissHolidays.map((holiday, index) => {
            const isAlreadyAdded = holidays.some(h => 
              h.name === holiday.name && h.start_date === holiday.start_date
            );
            
            return (
              <motion.button
                key={index}
                onClick={() => !isAlreadyAdded && addSwissHoliday(holiday)}
                disabled={isAlreadyAdded}
                whileHover={{ scale: isAlreadyAdded ? 1 : 1.02 }}
                whileTap={{ scale: isAlreadyAdded ? 1 : 0.98 }}
                className={`p-3 rounded-lg border text-left transition-colors ${
                  isAlreadyAdded
                    ? 'bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed'
                    : 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                }`}
              >
                <div className="font-medium">{holiday.name}</div>
                <div className="text-sm">
                  {format(parseISO(holiday.start_date), 'd. MMMM yyyy', { locale: de })}
                </div>
                {isAlreadyAdded && (
                  <div className="text-xs text-gray-500 mt-1">✓ Bereits hinzugefügt</div>
                )}
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Ferienperioden hinzufügen */}
      <div className="mb-8 bg-white rounded-2xl shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
          <SafeIcon icon={FiUmbrella} className="mr-2 text-blue-500" />
          Häufige Ferienperioden {currentYear}
        </h2>
        <p className="text-gray-600 mb-4">
          Fügen Sie typische Ferienperioden hinzu:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {commonVacations.map((vacation, index) => {
            const isAlreadyAdded = holidays.some(h => 
              h.name === vacation.name && 
              h.start_date === vacation.start_date &&
              h.end_date === vacation.end_date
            );
            
            return (
              <motion.button
                key={index}
                onClick={() => !isAlreadyAdded && addCommonVacation(vacation)}
                disabled={isAlreadyAdded}
                whileHover={{ scale: isAlreadyAdded ? 1 : 1.02 }}
                whileTap={{ scale: isAlreadyAdded ? 1 : 0.98 }}
                className={`p-3 rounded-lg border text-left transition-colors ${
                  isAlreadyAdded
                    ? 'bg-gray-100 text-gray-500 border-gray-200 cursor-not-allowed'
                    : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                }`}
              >
                <div className="font-medium">{vacation.name}</div>
                <div className="text-sm">
                  {format(parseISO(vacation.start_date), 'd. MMM', { locale: de })} - {' '}
                  {format(parseISO(vacation.end_date), 'd. MMM yyyy', { locale: de })}
                </div>
                <div className="text-xs text-blue-600 mt-1">{vacation.description}</div>
                {isAlreadyAdded && (
                  <div className="text-xs text-gray-500 mt-1">✓ Bereits hinzugefügt</div>
                )}
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Formular für Erstellen/Bearbeiten */}
      {isCreating && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-md p-6 mb-8"
        >
          <h2 className="text-xl font-bold text-gray-800 mb-6">
            {editingHoliday ? 'Eintrag bearbeiten' : 'Neuen Eintrag erstellen'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Typ auswählen */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Typ *
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, holiday_type: 'holiday', end_date: '' }))}
                  className={`flex items-center justify-center space-x-2 py-3 px-4 rounded-xl border transition-colors ${
                    formData.holiday_type === 'holiday'
                      ? 'bg-orange-500 text-white border-orange-500'
                      : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  <SafeIcon icon={FiSun} />
                  <span>Feiertag (1 Tag)</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, holiday_type: 'vacation' }))}
                  className={`flex items-center justify-center space-x-2 py-3 px-4 rounded-xl border transition-colors ${
                    formData.holiday_type === 'vacation'
                      ? 'bg-blue-500 text-white border-blue-500'
                      : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  <SafeIcon icon={FiUmbrella} />
                  <span>Ferien (Zeitraum)</span>
                </button>
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={formData.holiday_type === 'vacation' ? 'z.B. Sommerferien, Betriebsurlaub' : 'z.B. Weihnachten, Firmenfest'}
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>

            {/* Startdatum */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {formData.holiday_type === 'vacation' ? 'Startdatum *' : 'Datum *'}
              </label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                  errors.start_date ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.start_date && <p className="text-red-500 text-sm mt-1">{errors.start_date}</p>}
            </div>

            {/* Enddatum (nur für Ferien) */}
            {formData.holiday_type === 'vacation' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enddatum *
                </label>
                <input
                  type="date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                    errors.end_date ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.end_date && <p className="text-red-500 text-sm mt-1">{errors.end_date}</p>}
              </div>
            )}

            {/* Beschreibung */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Beschreibung (optional)
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                placeholder="Zusätzliche Informationen..."
              />
            </div>

            {/* Optionen */}
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
                />
                <label className="ml-2 text-sm font-medium text-gray-700">
                  Aktiv (blockiert Menüs an diesen Tagen)
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_recurring"
                  checked={formData.is_recurring}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
                />
                <label className="ml-2 text-sm font-medium text-gray-700">
                  Jährlich wiederkehrend
                </label>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex space-x-4">
              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`flex-1 flex items-center justify-center space-x-2 bg-primary-500 text-white py-3 rounded-xl font-semibold hover:bg-primary-600 transition-colors duration-300 ${
                  isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? (
                  <>
                    <SafeIcon icon={FiLoader} className="animate-spin" />
                    <span>Wird gespeichert...</span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiSave} />
                    <span>{editingHoliday ? 'Aktualisieren' : 'Erstellen'}</span>
                  </>
                )}
              </motion.button>

              <motion.button
                type="button"
                onClick={handleCancel}
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 flex items-center justify-center space-x-2 bg-gray-200 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-colors duration-300"
              >
                <SafeIcon icon={FiX} />
                <span>Abbrechen</span>
              </motion.button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Einträge-Liste */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-800">
            Einträge {currentYear} ({currentYearEntries.length})
            {filterType !== 'all' && ` - ${filterType === 'holiday' ? 'Feiertage' : 'Ferien'}`}
          </h3>
        </div>

        {isLoading ? (
          <div className="p-8 text-center">
            <SafeIcon icon={FiLoader} className="mx-auto text-4xl text-primary-500 animate-spin mb-4" />
            <p className="text-gray-600">Einträge werden geladen...</p>
          </div>
        ) : currentYearEntries.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {currentYearEntries.map((entry) => (
              <motion.div
                key={entry.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div className={`w-4 h-4 rounded-full ${
                        entry.is_active ? 'bg-red-500' : 'bg-gray-300'
                      }`}></div>
                      
                      <div className={`p-2 rounded-lg ${
                        entry.holiday_type === 'vacation' 
                          ? 'bg-blue-100 text-blue-600' 
                          : 'bg-orange-100 text-orange-600'
                      }`}>
                        <SafeIcon icon={entry.holiday_type === 'vacation' ? FiUmbrella : FiSun} />
                      </div>
                      
                      <div>
                        <h4 className={`font-semibold ${
                          entry.is_active ? 'text-gray-900' : 'text-gray-500'
                        }`}>
                          {entry.name}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {formatDateRange(entry)}
                        </p>
                        {entry.description && (
                          <p className="text-xs text-gray-500 mt-1">{entry.description}</p>
                        )}
                        {entry.holiday_type === 'vacation' && (
                          <p className="text-xs text-blue-600 mt-1">
                            <SafeIcon icon={FiClock} className="inline mr-1" />
                            {calculateDuration(entry)} Tage
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      entry.holiday_type === 'vacation' 
                        ? 'bg-blue-100 text-blue-600' 
                        : 'bg-orange-100 text-orange-600'
                    }`}>
                      {entry.holiday_type === 'vacation' ? 'Ferien' : 'Feiertag'}
                    </span>
                    
                    {entry.is_recurring && (
                      <span className="text-xs bg-purple-100 text-purple-600 px-2 py-1 rounded-full">
                        Jährlich
                      </span>
                    )}
                    
                    <motion.button
                      onClick={() => toggleHoliday(entry)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`p-2 rounded-lg transition-colors ${
                        entry.is_active
                          ? 'bg-red-100 text-red-600 hover:bg-red-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      title={entry.is_active ? 'Deaktivieren' : 'Aktivieren'}
                    >
                      <SafeIcon icon={entry.is_active ? FiToggleRight : FiToggleLeft} className="text-xl" />
                    </motion.button>

                    <motion.button
                      onClick={() => handleEdit(entry)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                      title="Bearbeiten"
                    >
                      <SafeIcon icon={FiEdit2} />
                    </motion.button>

                    <motion.button
                      onClick={() => handleDelete(entry.id)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                      title="Löschen"
                    >
                      <SafeIcon icon={FiTrash2} />
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <SafeIcon icon={FiCalendar} className="mx-auto text-4xl text-gray-400 mb-4" />
            <p className="text-gray-500 mb-4">
              Keine Einträge für {currentYear} gefunden
              {filterType !== 'all' && ` (${filterType === 'holiday' ? 'Feiertage' : 'Ferien'})`}
            </p>
            <motion.button
              onClick={() => setIsCreating(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700"
            >
              <SafeIcon icon={FiPlus} />
              <span>Ersten Eintrag erstellen</span>
            </motion.button>
          </div>
        )}
      </div>

      {/* Info-Box */}
      <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-6">
        <div className="flex items-center space-x-2 text-amber-800 mb-3">
          <SafeIcon icon={FiInfo} />
          <span className="font-medium">Wichtige Hinweise:</span>
        </div>
        <ul className="text-amber-700 text-sm space-y-1">
          <li>• <strong>Feiertage:</strong> Einzelne Tage, an denen keine Menüs verfügbar sind</li>
          <li>• <strong>Ferien:</strong> Zeiträume (von-bis), in denen der Betrieb geschlossen ist</li>
          <li>• Aktive Einträge blockieren automatisch die Menü-Bestellung für die entsprechenden Daten</li>
          <li>• Jährlich wiederkehrende Einträge werden automatisch für kommende Jahre übernommen</li>
          <li>• Bestehende Anmeldungen werden von Änderungen nicht beeinflusst</li>
          <li>• Feiertage und Ferien haben Vorrang vor der normalen Tage-Konfiguration</li>
        </ul>
      </div>
    </div>
  );
};

export default HolidayConfiguration;